import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchLiveGatewayMetrics,
  ILiveGatewayMetrics,
} from "./LiveGatewayService";
import { IPageable } from "../types/table/interfaces.type";

const METRICS_QUERY_KEY = ["liveGatewayMetrics", JSON.stringify({})] as const;

function useMetricsQuery(enabled: boolean = true) {
  return useQuery<IPageable<ILiveGatewayMetrics>>({
    queryKey: METRICS_QUERY_KEY,
    queryFn: () => fetchLiveGatewayMetrics({}),
    refetchInterval: 1000,
    enabled,
  });
}

export function useLiveBind(
  id: string | number | undefined | null,
): number | string | undefined {
  const key = id === undefined || id === null ? "" : String(id);
  const { data } = useQuery<
    IPageable<ILiveGatewayMetrics>,
    Error,
    number | string | undefined
  >({
    queryKey: METRICS_QUERY_KEY,
    queryFn: () => fetchLiveGatewayMetrics({}),
    refetchInterval: 1000,
    enabled: !!key,
    select: (d) =>
      d?.content?.find((e) => String(e.id) === key)?.binds ?? undefined,
  });
  return data;
}

export function useLiveBindsPolling(enabled: boolean = true) {
  const queryClient = useQueryClient();
  const { data } = useMetricsQuery(enabled);
  const prevUpStatusRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!data?.content) return;
    const current: Record<string, boolean> = {};
    let statusChanged = false;

    for (const entry of data.content) {
      const firstChar = String(entry.binds ?? "0").charAt(0);
      const isUp = Number(firstChar) > 0;
      const idKey = String(entry.id);
      current[idKey] = isUp;
      if (
        prevUpStatusRef.current[idKey] !== undefined &&
        prevUpStatusRef.current[idKey] !== isUp
      ) {
        statusChanged = true;
      }
    }
    prevUpStatusRef.current = current;

    if (statusChanged) {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    }
  }, [data, queryClient]);
}
